import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { string } from "yup";

import CheckmarkCircle from "components/icons/CheckmarkCircle";
import Warning from "components/icons/Warning";
import styles from "components/licenses/view-import-license-keys-view.scss";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import loginStyle from "components/login/login.scss";
import Modal from "components/modal/Modal";
import { LOGIN_ROUTE } from "components/router/Routes";
import Heading from "components/typography/heading/Heading";
import { TextBlock } from "components/typography/textBlock/TextBlock";
import { EMAIL_MAX_LENGTH } from "domain/globalConstants";
import { authenticationService } from "services/security/AuthenticationService";
import buttons from "styles/buttons.scss";
import formStyle from "styles/form.scss";
import { useTheme } from "utils/useTheme";

const EMAIL_VALIDATOR = string().required().email().max(EMAIL_MAX_LENGTH);

interface EmailContext {
    address: string;
    valid: boolean;
    error: boolean;
}

interface Confirmation {
    visible: boolean;
    done: boolean;
    success: boolean;
}

interface Props {
    isOpen: boolean;
    onClose: () => void;
    isSuccess: boolean;
    errorMessage?: string;
    userEmail?: string;
    onConfirmationChange?: (visible: boolean) => void;
    isNewUserFlow?: boolean;
}

const ValidatePasswordResetLink = (props: Props): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();
    const history = useHistory();
    const [emailContext, setEmailContext] = React.useState<EmailContext>(() => {
        const initialEmail = props.userEmail || "";
        let isValid = false;

        if (initialEmail) {
            try {
                EMAIL_VALIDATOR.validateSync(initialEmail.trim());
                isValid = true;
            } catch {
                isValid = false;
            }
        }

        return {
            address: initialEmail,
            valid: isValid,
            error: false,
        };
    });

    const [confirmation, setConfirmation] = React.useState<Confirmation>({
        visible: false,
        done: false,
        success: false,
    });

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const email = event.target.value;
        const trimmed = email.trim();
        const shortEnough = trimmed.length <= 255;
        let valid = shortEnough;
        try {
            EMAIL_VALIDATOR.validateSync(trimmed);
        } catch {
            valid = false;
        }
        if (shortEnough) {
            setEmailContext({
                address: email,
                valid,
                error: valid || email.length === 0 ? false : emailContext.error,
            });
        }
    };

    React.useEffect(() => {
        const timer = setTimeout(() => {
            if (emailContext.address !== "" && !emailContext.valid) {
                setEmailContext({
                    address: emailContext.address,
                    valid: emailContext.valid,
                    error: true,
                });
            }
        }, 700);
        return () => clearTimeout(timer);
    }, [emailContext.address, emailContext.valid, props.userEmail]);

    React.useEffect(() => {
        if (props.userEmail) {
            const trimmed = props.userEmail.trim();
            try {
                EMAIL_VALIDATOR.validateSync(trimmed);
                setEmailContext({
                    address: props.userEmail,
                    valid: true,
                    error: false,
                });
            } catch {
                setEmailContext({
                    address: props.userEmail,
                    valid: false,
                    error: false,
                });
            }
        }
    }, [props.userEmail]);

    const onSendResetClick = () => {
        props.onClose();
        setConfirmation({ visible: true, done: false, success: false });
        props.onConfirmationChange?.(true);

        authenticationService
            .requestPasswordReset(emailContext.address)
            .then(() => {
                setConfirmation({ visible: true, done: true, success: true });
                setEmailContext({ address: "", valid: false, error: false });
            })
            .catch(() => {
                setConfirmation({ visible: true, done: true, success: false });
            });
    };

    const hideConfirmation = () => {
        setConfirmation({ visible: false, done: false, success: false });
        props.onConfirmationChange?.(false);
        setTimeout(() => {
            history.push(LOGIN_ROUTE.path);
        }, 100);
    };

    const validationError = !emailContext.error ? (
        <div className={formStyle.blankError}></div>
    ) : (
        <div className={formStyle.error}>{t("Common.invalidEmail")}</div>
    );

    return (
        <>
            <Modal
                isOpen={props.isOpen}
                hideModal={props.onClose}
                modalTitle={t("TemporaryPasswordValidationFailureModal.title")}
                titleIcon={
                    <span className={styles.warningIconWrapper}>
                        <Warning
                            color={theme.secondaryWarningBackgroundColor}
                            width={20}
                            height={20}
                            strokeColor="#000"
                        />
                    </span>
                }
                closeButton={true}
                overlayBackgroundImage={true}
            >
                <div className={loginStyle.resetPasswordValidationText}>
                    <div>{t("TemporaryPasswordValidationFailureModal.description")}</div>
                    <div>
                        <div className={classNames({ [loginStyle.inputError]: emailContext.error })}>
                            <input
                                type="email"
                                className={loginStyle.enterEmail}
                                onChange={onChange}
                                value={emailContext.address}
                                max={EMAIL_MAX_LENGTH}
                                autoFocus
                            />
                            {validationError}
                        </div>
                    </div>
                    <div className={loginStyle.loginModalSubmitButton}>
                        <button
                            onClick={onSendResetClick}
                            className={buttons.primaryButtonWithoutIcon}
                            disabled={!emailContext.valid}
                        >
                            {t("Login.forgotPasswordModal.submitButton")}
                        </button>
                    </div>
                </div>
            </Modal>

            {confirmation.visible && !confirmation.done && (
                <Modal isOpen={true} hideModal={() => {}} overlayBackgroundImage={true} closeButton={true}>
                    <LoadingIndicator />
                </Modal>
            )}

            {confirmation.visible && confirmation.done && (
                <Modal isOpen={true} hideModal={hideConfirmation} closeButton={true} overlayBackgroundImage={true}>
                    <div className={loginStyle.confirmationContainer}>
                        <div className={loginStyle.iconCenter}>
                            {confirmation.success ? (
                                <CheckmarkCircle />
                            ) : (
                                <Warning color={theme.errorBackgroundColor} width={30} height={30} strokeColor="#fff" />
                            )}
                        </div>
                        <div className={loginStyle.confirmationHeading}>
                            <Heading tag="h2">
                                {confirmation.success
                                    ? t("TemporaryPasswordValidationFailureModal.resetLinkSent")
                                    : t("TemporaryPasswordValidationFailureModal.title")}
                            </Heading>
                        </div>
                        <TextBlock alignCenter={true}>
                            {confirmation.success
                                ? t("TemporaryPasswordValidationFailureModal.checkYourEmail")
                                : t("Login.forgotPasswordConfirmationModal.failureMessage")}
                        </TextBlock>
                        <div className={loginStyle.confirmationButtonCenter}>
                            <button onClick={hideConfirmation} className={buttons.primaryButtonWithoutIcon}>
                                {t("TemporaryPasswordValidationFailureModal.gotoLoginPage")}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default ValidatePasswordResetLink;
